import React, { useState } from "react";
import styled from "styled-components";
import { theme } from "../styles/theme";
import EmailIcon from "@mui/icons-material/Email";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import BusinessIcon from "@mui/icons-material/Business";
import SubjectIcon from "@mui/icons-material/Subject";
import MessageIcon from "@mui/icons-material/Message";

const ContactFormContainer = styled.div`
  background-color: ${theme.colors.white};
  border-radius: 15px;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  max-width: 800px;
  margin: 0 auto;

  h2 {
    font-size: 2rem;
    margin-bottom: 1.5rem;
    color: ${theme.colors.primary};
    text-align: center;

    @media (max-width: ${theme.breakpoints.mobile}) {
      font-size: 1.5rem;
    }
  }

  .form-description {
    text-align: center;
    margin-bottom: 2rem;
    color: ${theme.colors.gray};
    font-size: 0.95rem;
  }

  .form-group {
    margin-bottom: 1.5rem;
    position: relative;

    label {
      display: block;
      margin-bottom: 0.5rem;
      font-weight: 500;
      color: ${theme.colors.dark};
    }

    .input-icon {
      position: absolute;
      left: 12px;
      top: 42px;
      color: ${theme.colors.gray};
      font-size: 1.2rem;
    }

    input,
    textarea {
      width: 100%;
      padding: 0.8rem 0.8rem 0.8rem 2.5rem;
      border: 1px solid #ddd;
      border-radius: 8px;
      font-family: inherit;
      font-size: 1rem;
      transition: all 0.3s ease;

      &:focus {
        outline: none;
        border-color: ${theme.colors.primary};
        box-shadow: 0 0 0 3px rgba(0, 119, 182, 0.1);
      }
    }

    textarea {
      min-height: 150px;
      resize: vertical;
      padding-left: 0.8rem; /* No icon for textarea */
    }
  }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;

    @media (max-width: ${theme.breakpoints.mobile}) {
      grid-template-columns: 1fr;
    }
  }

  .submit-btn {
    background: linear-gradient(
      135deg,
      ${theme.colors.primary},
      ${theme.colors.secondary}
    );
    color: ${theme.colors.white};
    border: none;
    padding: 0.8rem 2rem;
    border-radius: 50px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: block;
    margin: 2rem auto 0;
    box-shadow: 0 4px 15px rgba(0, 119, 182, 0.3);
    display: flex;
    align-items: center;
    gap: 0.5rem;

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 6px 20px rgba(0, 119, 182, 0.4);
    }
  }

  .form-message {
    text-align: center;
    margin-top: 1.5rem;
    padding: 1rem;
    border-radius: 8px;
    font-weight: 500;

    &.info {
      background-color: rgba(23, 162, 184, 0.1);
      color: #17a2b8;
    }
  }
`;

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formMessage, setFormMessage] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const emailBody = `
    Name: ${formData.name}
    Email: ${formData.email}
    Phone: ${formData.phone}
    Company: ${formData.company}

    Message:${formData.message}`.trim();
    
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=support@amplelark.com&su=${encodeURIComponent(formData.subject)}&body=${encodeURIComponent(emailBody)}`;
    
    window.open(gmailUrl, "_blank");

    setFormMessage("Your default email client has been opened with your message. Please send the email to complete your inquiry.");
    setIsSubmitting(false);
    
    setFormData({
      name: "",
      email: "",
      phone: "",
      company: "",
      subject: "",
      message: "",
    });
  };

  return (
    <ContactFormContainer>
      <h2>Get In Touch</h2>
      <p className="form-description">
        Fill out the form below and we'll open your email client with your message pre-filled.
      </p>
      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="name">Full Name *</label>
            <PersonIcon className="input-icon" />
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="email">Email Address *</label>
            <EmailIcon className="input-icon" />
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="phone">Phone Number</label>
            <PhoneIcon className="input-icon" />
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="company">Company</label>
            <BusinessIcon className="input-icon" />
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
            />
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="subject">Subject *</label>
          <SubjectIcon className="input-icon" />
          <input
            type="text"
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="message">Message *</label>
          <MessageIcon className="input-icon" style={{ top: "38px" }} />
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
          ></textarea>
        </div>
        <button type="submit" className="submit-btn" disabled={isSubmitting}>
          <EmailIcon />
          {isSubmitting ? "Opening Email..." : "Open Email Client"}
        </button>
        {formMessage && (
          <div className={`form-message info`}>{formMessage}</div>
        )}
      </form>
    </ContactFormContainer>
  );
};

export default ContactForm;